import { LocalDate } from "@js-joda/core";
import { type GetTimeReportsV2SummaryResponse } from "services/api/company/timeReports.service";
import {
  formatSummaryDuration,
  formatSummaryDurationSum,
  parseSignedDurationToMinutes,
} from "web-apps/company/utils/timeAndDate";
import { type TimeReportDto } from "model/TimeReport";
import { type Order } from "model/Analytics";
import {
  type AnalyticsPeriod,
  type OperatingHours,
  isOrderInBusinessDay,
  getBusinessDate,
  getWeekBounds,
  parseTimestamp,
  walkTimeReportSlots,
} from "./analytics.operatingHours";

// ─── Personnel cost ───────────────────────────────────────────────────────────

/**
 * Walks each time report hour-by-hour and sums hourlyRate × hours only for
 * slots that fall within the operating window. The result is then scaled by
 * the API's cost multiplier to produce the accurate stat card value.
 */
export const computeBusinessHourGross = (
  reports: TimeReportDto[],
  period: AnalyticsPeriod,
  selectedDate: LocalDate,
  operatingHours: OperatingHours,
): number => {
  let gross = 0;
  if (period === "day") {
    for (const report of reports) {
      walkTimeReportSlots(report, (slotStart, fractionHours) => {
        if (isOrderInBusinessDay(slotStart, selectedDate, operatingHours)) {
          gross += report.hourlyRate * fractionHours;
        }
      });
    }
    return gross;
  }
  // For all non-day periods: use business-date attribution so that overnight
  // tail slots are correctly assigned to the previous calendar day, preventing
  // both undercounts (slots dropped) and overcounts (slots leaking from the
  // adjacent period).
  const { fromDate, toDate } = getDateRange(selectedDate, period);
  for (const report of reports) {
    walkTimeReportSlots(report, (slotStart, fractionHours) => {
      const bizDate = getBusinessDate(slotStart, operatingHours);
      if (bizDate && !bizDate.isBefore(fromDate) && !bizDate.isAfter(toDate)) {
        gross += report.hourlyRate * fractionHours;
      }
    });
  }
  return gross;
};

// ─── Date / period utilities ──────────────────────────────────────────────────

export const getDateRange = (
  date: LocalDate,
  period: AnalyticsPeriod,
): { fromDate: LocalDate; toDate: LocalDate } => {
  if (period === "week") {
    const { weekStart, weekEnd } = getWeekBounds(date);
    return { fromDate: weekStart, toDate: weekEnd };
  }
  if (period === "month") {
    return {
      fromDate: date.withDayOfMonth(1),
      toDate: date.withDayOfMonth(date.lengthOfMonth()),
    };
  }
  if (period === "year") {
    return {
      fromDate: LocalDate.of(date.year(), 1, 1),
      toDate: LocalDate.of(date.year(), 12, 31),
    };
  }
  return { fromDate: date, toDate: date };
};

export const toYearMonth = (date: LocalDate): number =>
  parseInt(`${date.year()}${String(date.monthValue()).padStart(2, "0")}`);

/**
 * Returns the equivalent date one period earlier — the basis for every
 * period-over-period comparison. Matches PeriodNavigator's "previous" step
 * exactly, so "vs previous" always means the period the back arrow would show.
 */
export const getPreviousPeriodDate = (
  date: LocalDate,
  period: AnalyticsPeriod,
): LocalDate => {
  if (period === "week") return date.minusWeeks(1);
  if (period === "month") return date.minusMonths(1);
  if (period === "year") return date.minusYears(1);
  return date.minusDays(1);
};

/**
 * Narrows a raw order list to those whose business date falls inside the
 * period. Extracted so the current and previous periods are filtered by
 * identical rules rather than a duplicated inline predicate.
 */
export const filterOrdersForPeriod = (
  orders: Order[],
  period: AnalyticsPeriod,
  selectedDate: LocalDate,
  operatingHours: OperatingHours,
): Order[] => {
  const { fromDate, toDate } = getDateRange(selectedDate, period);
  return orders.filter((o) => {
    const ts = parseTimestamp(o.timestamp);
    if (period === "day") {
      return isOrderInBusinessDay(ts, selectedDate, operatingHours);
    }
    // week / month / year: verify the order's business date falls within the
    // period. A post-midnight order on the first calendar day can belong to
    // the previous period's overnight window and must be excluded.
    const bizDate = getBusinessDate(ts, operatingHours);
    return (
      bizDate !== null &&
      !bizDate.isBefore(fromDate) &&
      !bizDate.isAfter(toDate)
    );
  });
};

/**
 * Returns true when the selected period contains at least one overnight window
 * (closeHour < openHour), meaning the API date range must be extended by one day.
 */
export const isOvernightActive = (
  period: AnalyticsPeriod,
  selectedDate: LocalDate,
  operatingHours: OperatingHours,
): boolean => {
  if (period === "day") {
    const idx = selectedDate.dayOfWeek().value() - 1;
    const dh = operatingHours[idx];
    return dh.enabled && dh.closeHour < dh.openHour;
  }
  return operatingHours.some((dh) => dh.enabled && dh.closeHour < dh.openHour);
};

export const formatDateLabel = (
  date: LocalDate,
  period: AnalyticsPeriod,
  todayLabel: string,
  yesterdayLabel: string,
): string => {
  if (period === "week") {
    const { weekStart, weekEnd } = getWeekBounds(date);
    const fmt = (d: LocalDate) =>
      `${String(d.dayOfMonth()).padStart(2, "0")}/${String(d.monthValue()).padStart(2, "0")}`;
    return `${fmt(weekStart)} – ${fmt(weekEnd)}`;
  }
  if (period === "month") {
    return `${String(date.monthValue()).padStart(2, "0")}/${date.year()}`;
  }
  if (period === "year") {
    return String(date.year());
  }
  const today = LocalDate.now();
  if (date.isEqual(today)) return todayLabel;
  if (date.isEqual(today.minusDays(1))) return yesterdayLabel;
  return `${String(date.dayOfMonth()).padStart(2, "0")}/${String(date.monthValue()).padStart(2, "0")}`;
};

// ─── Formatting ───────────────────────────────────────────────────────────────

export const formatCost = (amount: number): string =>
  `${Math.round(amount).toLocaleString("sv-SE")} kr`;

// ─── Card order ───────────────────────────────────────────────────────────────

export const CARD_ORDER_STORAGE_KEY = "analytics_card_order";

export const DEFAULT_CARD_ORDER = [
  "sales",
  "personnelCost",
  "customCosts",
  "personnelCostPct",
  "plannedHours",
  "reportedHours",
  "salesPerHour",
  "tips",
  "result",
];

export const initCardOrder = (): string[] => {
  try {
    const stored = localStorage.getItem(CARD_ORDER_STORAGE_KEY);
    if (stored) {
      const parsed: string[] = JSON.parse(stored);
      const valid = parsed.filter((l) => DEFAULT_CARD_ORDER.includes(l));
      const newCards = DEFAULT_CARD_ORDER.filter((l) => !parsed.includes(l));
      return [...valid, ...newCards];
    }
  } catch {}
  return [...DEFAULT_CARD_ORDER];
};

/** Returns a new key array with the dragged card moved to the drop position. */
export const reorderCards = (
  orderedKeys: string[],
  dragIndex: number,
  dropSlot: number,
): string[] => {
  const newOrder = [...orderedKeys];
  const adjustedSlot = dragIndex < dropSlot ? dropSlot - 1 : dropSlot;
  const [moved] = newOrder.splice(dragIndex, 1);
  newOrder.splice(adjustedSlot, 0, moved);
  return newOrder;
};

// ─── Stat cards ───────────────────────────────────────────────────────────────

/**
 * Period-over-period delta shown beneath a card's value. `sentiment` is
 * deliberately separate from direction: a rise in sales is good, a rise in
 * personnel cost is not, and hours are neither.
 */
export interface StatCardComparison {
  /** Formatted delta, e.g. "+12.4%" or "-2.1 pp". */
  label: string;
  sentiment: "positive" | "negative" | "neutral";
}

export interface StatCardData {
  key: string;
  label: string;
  value: string;
  isLoading: boolean;
  /** null when there's no comparable previous-period figure. */
  comparison: StatCardComparison | null;
}

/** Which direction of change counts as an improvement for a given metric. */
type Polarity = "up-good" | "up-bad" | "neutral";

/**
 * Builds the delta between a metric's current and previous-period values.
 *
 * `mode: "relative"` expresses the change as a percentage of the previous
 * value — the right reading for money and hours. `mode: "points"` reports the
 * raw difference in percentage points, used for metrics that are themselves
 * percentages (where "+10%" would be ambiguous between a relative rise and a
 * ten-point rise).
 *
 * Returns null when no meaningful comparison exists: either figure missing, or
 * a relative change against a zero baseline (which is undefined, not infinite).
 */
const buildComparison = (
  current: number | null,
  previous: number | null,
  polarity: Polarity,
  mode: "relative" | "points" = "relative",
): StatCardComparison | null => {
  if (current === null || previous === null) return null;

  const delta = current - previous;

  let label: string;
  if (mode === "points") {
    // Rounds to one decimal, so anything under 0.05 reads as no change.
    if (Math.abs(delta) < 0.05) return { label: "0 pp", sentiment: "neutral" };
    label = `${delta > 0 ? "+" : "-"}${Math.abs(delta).toFixed(1)} pp`;
  } else {
    if (previous === 0) return null;
    // Math.abs on the denominator keeps the sign meaningful when the previous
    // value was negative (a loss shrinking is still an improvement).
    const pct = (delta / Math.abs(previous)) * 100;
    if (Math.abs(pct) < 0.05) return { label: "0%", sentiment: "neutral" };
    label = `${pct > 0 ? "+" : "-"}${Math.abs(pct).toFixed(1)}%`;
  }

  const sentiment: StatCardComparison["sentiment"] =
    polarity === "neutral"
      ? "neutral"
      : delta > 0 === (polarity === "up-good")
        ? "positive"
        : "negative";

  return { label, sentiment };
};

/** Total minutes a summary counts as "reported" (approved + pending). */
const reportedMinutes = (
  summary: GetTimeReportsV2SummaryResponse | null,
): number | null => {
  if (!summary) return null;
  return (
    (parseSignedDurationToMinutes(summary.approved.time) ?? 0) +
    (parseSignedDurationToMinutes(summary.pending.time) ?? 0)
  );
};

/** Translated display labels for each stat card — resolved by the component. */
export interface StatCardLabels {
  sales: string;
  personnelCost: string;
  customCosts: string;
  personnelCostPct: string;
  plannedHours: string;
  reportedHours: string;
  salesPerHour: string;
  tips: string;
  result: string;
}

/** The same set of raw figures, resolved for the preceding period. */
export interface PreviousPeriodValues {
  sales: number | null;
  tips: number | null;
  personnelCost: number | null;
  customCostsTotal: number;
  summary: GetTimeReportsV2SummaryResponse | null;
}

export interface BuildStatCardsParams {
  labels: StatCardLabels;
  sales: number | null;
  tips: number | null;
  personnelCost: number | null;
  customCostsTotal: number;
  summary: GetTimeReportsV2SummaryResponse | null;
  /** Previous-period figures for the comparison line; null while still loading. */
  previous: PreviousPeriodValues | null;
  operatingHours: OperatingHours;
  period: AnalyticsPeriod;
  selectedDate: LocalDate;
  ordersLoading: boolean;
  personnelCostLoading: boolean;
  summaryLoading: boolean;
}

/** Sorts stat cards according to the user's saved card order. */
export const sortStatCards = (
  stats: StatCardData[],
  cardOrder: string[],
): StatCardData[] =>
  [...stats].sort((a, b) => {
    const ai = cardOrder.indexOf(a.key);
    const bi = cardOrder.indexOf(b.key);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

// ── Private card builders ─────────────────────────────────────────────────────

const computeOperatingHoursDuration = (
  operatingHours: OperatingHours,
  period: AnalyticsPeriod,
  selectedDate: LocalDate,
): number => {
  const dayDuration = (idx: number): number => {
    const dh = operatingHours[idx];
    if (!dh.enabled) return 0;
    // e.g. open=10 close=23 → 13 h; open=22 close=3 → 5 h (overnight)
    return dh.openHour === dh.closeHour
      ? 24
      : (dh.closeHour - dh.openHour + 24) % 24;
  };
  if (period === "day") {
    // joda DayOfWeek.value(): 1=Mon…7=Sun → index 0–6
    return dayDuration(selectedDate.dayOfWeek().value() - 1);
  }
  if (period === "week") {
    return operatingHours.reduce((sum, _, i) => sum + dayDuration(i), 0);
  }
  if (period === "month") {
    const firstDay = selectedDate.withDayOfMonth(1);
    const daysInMonth = selectedDate.lengthOfMonth();
    let total = 0;
    for (let d = 0; d < daysInMonth; d++) {
      total += dayDuration(firstDay.plusDays(d).dayOfWeek().value() - 1);
    }
    return total;
  }
  // year — iterate every calendar day to respect per-weekday operating hours
  const firstDay = LocalDate.of(selectedDate.year(), 1, 1);
  const daysInYear = LocalDate.of(selectedDate.year(), 12, 31).dayOfYear();
  let total = 0;
  for (let d = 0; d < daysInYear; d++) {
    total += dayDuration(firstDay.plusDays(d).dayOfWeek().value() - 1);
  }
  return total;
};

const buildPlannedHoursCard = (
  label: string,
  summary: GetTimeReportsV2SummaryResponse | null,
  previousSummary: GetTimeReportsV2SummaryResponse | null,
  isLoading: boolean,
): StatCardData => ({
  key: "plannedHours",
  label,
  value: summary ? formatSummaryDuration(summary.planned.time) : "–",
  isLoading,
  comparison: buildComparison(
    summary ? parseSignedDurationToMinutes(summary.planned.time) : null,
    previousSummary
      ? parseSignedDurationToMinutes(previousSummary.planned.time)
      : null,
    "neutral",
  ),
});

/**
 * "Reported" = approved + pending — any hours a worker has actually logged
 * via a time report, whether or not a manager has gotten around to approving
 * it yet. Distinct from "planned" (scheduled hours from the roster) and from
 * approved alone (which would undercount while approvals are still pending).
 */
const buildReportedHoursCard = (
  label: string,
  summary: GetTimeReportsV2SummaryResponse | null,
  previousSummary: GetTimeReportsV2SummaryResponse | null,
  isLoading: boolean,
): StatCardData => ({
  key: "reportedHours",
  label,
  value: summary
    ? formatSummaryDurationSum(summary.approved.time, summary.pending.time)
    : "–",
  isLoading,
  comparison: buildComparison(
    reportedMinutes(summary),
    reportedMinutes(previousSummary),
    "neutral",
  ),
});

const buildPersonnelCostCard = (
  label: string,
  cost: number | null,
  previousCost: number | null,
  isLoading: boolean,
): StatCardData => ({
  key: "personnelCost",
  label,
  value: cost !== null ? formatCost(cost) : "–",
  isLoading,
  comparison: buildComparison(cost, previousCost, "up-bad"),
});

// ── Main entry point ──────────────────────────────────────────────────────────

export const buildStatCards = (
  params: BuildStatCardsParams,
): StatCardData[] => {
  const {
    labels,
    sales,
    tips,
    personnelCost,
    customCostsTotal,
    summary,
    previous,
    operatingHours,
    period,
    selectedDate,
    ordersLoading,
    personnelCostLoading,
    summaryLoading,
  } = params;

  // ── Derived metrics, computed identically for both periods ──────────────────

  const derivePersonnelCostPct = (
    cost: number | null,
    revenue: number | null,
  ): number | null =>
    cost !== null && revenue !== null && revenue > 0
      ? (cost / revenue) * 100
      : null;

  const deriveSalesPerHour = (
    revenue: number | null,
    date: LocalDate,
  ): number | null => {
    const duration = computeOperatingHoursDuration(
      operatingHours,
      period,
      date,
    );
    return revenue !== null && duration > 0 ? revenue / duration : null;
  };

  const deriveResult = (
    revenue: number | null,
    cost: number | null,
    otherCosts: number,
  ): number | null =>
    revenue !== null && cost !== null ? revenue - cost - otherCosts : null;

  const personnelCostPct = derivePersonnelCostPct(personnelCost, sales);
  const salesPerHour = deriveSalesPerHour(sales, selectedDate);
  const result = deriveResult(sales, personnelCost, customCostsTotal);

  // The previous period's operating-hours duration is its own — February and
  // March have different day counts, so salesPerHour must not reuse the
  // current period's divisor.
  const previousDate = getPreviousPeriodDate(selectedDate, period);
  const prevPersonnelCostPct = previous
    ? derivePersonnelCostPct(previous.personnelCost, previous.sales)
    : null;
  const prevSalesPerHour = previous
    ? deriveSalesPerHour(previous.sales, previousDate)
    : null;
  const prevResult = previous
    ? deriveResult(
        previous.sales,
        previous.personnelCost,
        previous.customCostsTotal,
      )
    : null;

  return [
    {
      key: "sales",
      label: labels.sales,
      value: sales !== null ? formatCost(sales) : "–",
      isLoading: ordersLoading,
      comparison: buildComparison(sales, previous?.sales ?? null, "up-good"),
    },
    buildPersonnelCostCard(
      labels.personnelCost,
      personnelCost,
      previous?.personnelCost ?? null,
      personnelCostLoading,
    ),
    {
      key: "customCosts",
      label: labels.customCosts,
      value: formatCost(customCostsTotal),
      isLoading: false,
      comparison: buildComparison(
        customCostsTotal,
        previous?.customCostsTotal ?? null,
        "up-bad",
      ),
    },
    {
      key: "personnelCostPct",
      label: labels.personnelCostPct,
      value:
        personnelCostPct !== null ? `${personnelCostPct.toFixed(1)}%` : "–",
      isLoading: ordersLoading || personnelCostLoading,
      // Already a percentage — report the change in points, not as a
      // percentage of a percentage.
      comparison: buildComparison(
        personnelCostPct,
        prevPersonnelCostPct,
        "up-bad",
        "points",
      ),
    },
    buildPlannedHoursCard(
      labels.plannedHours,
      summary,
      previous?.summary ?? null,
      summaryLoading,
    ),
    buildReportedHoursCard(
      labels.reportedHours,
      summary,
      previous?.summary ?? null,
      summaryLoading,
    ),
    {
      key: "salesPerHour",
      label: labels.salesPerHour,
      value: salesPerHour !== null ? formatCost(salesPerHour) : "–",
      isLoading: ordersLoading,
      comparison: buildComparison(salesPerHour, prevSalesPerHour, "up-good"),
    },
    {
      key: "tips",
      label: labels.tips,
      value: tips !== null ? formatCost(tips) : "–",
      isLoading: ordersLoading,
      comparison: buildComparison(tips, previous?.tips ?? null, "up-good"),
    },
    {
      key: "result",
      label: labels.result,
      value: result !== null ? formatCost(result) : "–",
      isLoading: ordersLoading || personnelCostLoading,
      comparison: buildComparison(result, prevResult, "up-good"),
    },
  ];
};
